import { type FC, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    CheckCircle2,
    ChevronDown,
    Eye,
    EyeOff,
    Mail,
    Phone,
    UserPlus,
    X,
} from 'lucide-react'

export interface AddUserFormState {
    fullName: string
    email: string
    phoneNumber: string
    gender: 'MALE' | 'FEMALE' | ''
    internalRoleId: string
    country: string
    address: string
    password: string
    confirmPassword: string
}

interface AddUserModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit?: (payload: AddUserFormState) => void
    roleOptions?: Array<{ id: string; title: string }>
    isSubmitting?: boolean
}

const defaultForm: AddUserFormState = {
    fullName: '',
    email: '',
    phoneNumber: '',
    gender: '',
    internalRoleId: '',
    country: '',
    address: '',
    password: '',
    confirmPassword: '',
}

const SelectField = ({
    value,
    onChange,
    placeholder,
    options,
}: {
    value: string
    onChange: (value: string) => void
    placeholder: string
    options: string[]
}) => (
    <div className="relative">
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className="h-[52px] w-full appearance-none rounded-full border border-border bg-secondary px-4 text-sm text-foreground shadow-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/40"
        >
            <option value="">{placeholder}</option>
            {options.map(opt => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    </div>
)

export const AddUserModal: FC<AddUserModalProps> = ({
    open,
    onOpenChange,
    onSubmit,
    roleOptions = [],
    isSubmitting = false,
}) => {
    const [step, setStep] = useState<1 | 2>(1)
    const [form, setForm] = useState<AddUserFormState>(defaultForm)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const passwordChecks = useMemo(
        () => ({
            hasUppercase: /[A-Z]/.test(form.password),
            hasNumber: /\d/.test(form.password),
            hasLength: form.password.length >= 8,
        }),
        [form.password]
    )

    const passwordScore =
        Number(passwordChecks.hasUppercase) +
        Number(passwordChecks.hasNumber) +
        Number(passwordChecks.hasLength)

    const passwordsMatch =
        Boolean(form.password) && form.password === form.confirmPassword
    const hasPersonalInfo =
        !!form.fullName &&
        !!form.email &&
        !!form.phoneNumber &&
        !!form.gender &&
        !!form.internalRoleId &&
        !!form.country &&
        !!form.address
    const canSubmit = hasPersonalInfo && !!form.password && passwordsMatch

    const resetState = () => {
        setStep(1)
        setForm(defaultForm)
        setShowPassword(false)
        setShowConfirmPassword(false)
    }

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetState()
        }
        onOpenChange(nextOpen)
    }

    const handleSubmit = () => {
        if (!canSubmit || isSubmitting) return
        onSubmit?.(form)
    }

    const renderStepTabs = () => (
        <div className="flex flex-wrap gap-6 border-b px-6 pt-3">
            {[
                { id: 1 as const, label: 'Personal Information' },
                { id: 2 as const, label: 'Security' },
            ].map(tab => {
                const active = step === tab.id
                return (
                    <button
                        key={tab.id}
                        onClick={() => setStep(tab.id)}
                        className={`relative pb-3 text-sm font-semibold transition-colors ${
                            active
                                ? 'text-[#0F172A]'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <span
                            className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                                active
                                    ? 'bg-[#17A2B8] text-white'
                                    : 'bg-muted text-muted-foreground'
                            }`}
                        >
                            {tab.id}
                        </span>
                        {tab.label}
                        {active && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-[#17A2B8]" />
                        )}
                    </button>
                )
            })}
        </div>
    )

    const renderPersonalInfo = () => (
        <div className="space-y-4 px-6 pb-6 pt-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                        Full name
                    </label>
                    <Input
                        value={form.fullName}
                        placeholder="Fill in full name"
                        onChange={e =>
                            setForm(prev => ({
                                ...prev,
                                fullName: e.target.value,
                            }))
                        }
                        className="h-[52px] rounded-xl"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                        Email address
                    </label>
                    <div className="relative">
                        <Input
                            type="email"
                            value={form.email}
                            placeholder="Enter email"
                            onChange={e =>
                                setForm(prev => ({
                                    ...prev,
                                    email: e.target.value,
                                }))
                            }
                            className="h-[52px] rounded-xl pl-11"
                        />
                        <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">
                        Phone number
                    </label>
                    <div className="flex gap-3">
                        <div className="flex items-center justify-center rounded-full bg-[#E5F5F9] px-4 text-sm font-semibold text-[#17A2B8]">
                            +234
                        </div>
                        <div className="relative w-full">
                            <Input
                                value={form.phoneNumber}
                                placeholder="Enter your phone number"
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        phoneNumber: e.target.value,
                                    }))
                                }
                                className="h-[52px] rounded-xl pl-11"
                            />
                            <Phone className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <SelectField
                    value={form.gender}
                    onChange={value =>
                        setForm(prev => ({
                            ...prev,
                            gender: value as AddUserFormState['gender'],
                        }))
                    }
                    placeholder="Select gender"
                    options={['MALE', 'FEMALE']}
                />
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                        User role
                    </label>
                    <div className="relative">
                        <select
                            value={form.internalRoleId}
                            onChange={e =>
                                setForm(prev => ({
                                    ...prev,
                                    internalRoleId: e.target.value,
                                }))
                            }
                            className="h-[52px] w-full appearance-none rounded-full border border-border bg-secondary px-4 text-sm text-foreground shadow-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/40"
                        >
                            <option value="">
                                {roleOptions.length > 0
                                    ? 'Select user role'
                                    : 'No roles available'}
                            </option>
                            {roleOptions.map(role => (
                                <option key={role.id} value={role.id}>
                                    {role.title}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    </div>
                </div>
                <SelectField
                    value={form.country}
                    onChange={value =>
                        setForm(prev => ({ ...prev, country: value }))
                    }
                    placeholder="Country of residence"
                    options={['Nigeria', 'Ghana', 'Kenya', 'South Africa']}
                />
                <Input
                    value={form.address}
                    placeholder="Residential address"
                    onChange={e =>
                        setForm(prev => ({
                            ...prev,
                            address: e.target.value,
                        }))
                    }
                    className="h-[52px] rounded-xl md:col-span-2"
                />
            </div>
        </div>
    )

    const renderSecurity = () => (
        <div className="space-y-5 px-6 pb-6 pt-5">
            <div className="space-y-3">
                <div className="relative">
                    <Input
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        placeholder="Enter password"
                        onChange={e =>
                            setForm(prev => ({
                                ...prev,
                                password: e.target.value,
                            }))
                        }
                        className="h-[52px] rounded-xl pr-12"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute inset-y-0 right-4 flex items-center text-muted-foreground"
                    >
                        {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                        ) : (
                            <Eye className="h-4 w-4" />
                        )}
                    </button>
                </div>

                <div className="h-1.5 rounded-full bg-muted">
                    <div
                        className={`h-full rounded-full transition-all ${
                            passwordScore === 3
                                ? 'bg-[#1FC16B]'
                                : passwordScore === 2
                                  ? 'bg-[#FFB020]'
                                  : 'bg-[#FB3748]'
                        }`}
                        style={{
                            width: `${(passwordScore / 3) * 100}%`,
                        }}
                    />
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                    <p>Must contain at least:</p>
                    <ul className="space-y-1">
                        {[
                            {
                                label: 'At least 1 uppercase',
                                key: 'hasUppercase',
                                passed: passwordChecks.hasUppercase,
                            },
                            {
                                label: 'At least 1 number',
                                key: 'hasNumber',
                                passed: passwordChecks.hasNumber,
                            },
                            {
                                label: 'At least 8 characters',
                                key: 'hasLength',
                                passed: passwordChecks.hasLength,
                            },
                        ].map(rule => (
                            <li
                                key={rule.key}
                                className="flex items-center gap-2"
                            >
                                <CheckCircle2
                                    className={`h-4 w-4 ${
                                        rule.passed
                                            ? 'text-[#1FC16B]'
                                            : 'text-muted-foreground/40'
                                    }`}
                                />
                                <span
                                    className={
                                        rule.passed
                                            ? 'text-[#0F172A]'
                                            : 'text-muted-foreground'
                                    }
                                >
                                    {rule.label}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="space-y-3">
                <div className="relative">
                    <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={form.confirmPassword}
                        placeholder="Confirm password"
                        onChange={e =>
                            setForm(prev => ({
                                ...prev,
                                confirmPassword: e.target.value,
                            }))
                        }
                        className="h-[52px] rounded-xl pr-12"
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        className="absolute inset-y-0 right-4 flex items-center text-muted-foreground"
                    >
                        {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                        ) : (
                            <Eye className="h-4 w-4" />
                        )}
                    </button>
                </div>
                {form.confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-500">
                        Passwords do not match
                    </p>
                )}
            </div>
        </div>
    )

    const footerActions = () => (
        <div className="flex flex-col gap-3 border-t bg-white px-6 py-4 sm:flex-row sm:justify-end sm:gap-4">
            <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="h-12 flex-1 rounded-full sm:flex-none sm:min-w-[140px]"
            >
                Cancel
            </Button>
            {step === 1 ? (
                <Button
                    onClick={() => setStep(2)}
                    disabled={!hasPersonalInfo}
                    className="h-12 flex-1 rounded-full bg-[#17A2B8] text-white hover:bg-[#1493a7] sm:flex-none sm:min-w-[140px]"
                >
                    Next
                </Button>
            ) : (
                <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit || isSubmitting}
                    className="h-12 flex-1 rounded-full bg-[#17A2B8] text-white hover:bg-[#1493a7] sm:flex-none sm:min-w-[140px]"
                >
                    {isSubmitting ? 'Adding user...' : 'Add user'}
                </Button>
            )}
        </div>
    )

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-3xl sm:rounded-3xl"
            >
                <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <UserPlus className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    Add new user
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    Add to the staff you have at Ogaboss.
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                            className="h-8 w-8 rounded-full"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Tabs */}
                    {renderStepTabs()}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                        {step === 1 ? renderPersonalInfo() : renderSecurity()}
                    </div>

                    {/* Footer */}
                    {footerActions()}
                </div>
            </DialogContent>
        </Dialog>
    )
}
